/**
 * Kid Money — shared UI. Presentation only, no fetching. This module owns its own
 * copy of these primitives (it never reaches into another module's files).
 *
 * Warm and homey, kid-first: big targets, their own colour, a game feel. Money is
 * always formatted with money() from lib/api — never hand-built. Colour is never the
 * ONLY signal (every bucket and state carries a label and an emoji too).
 */
import { useEffect, type ReactNode } from 'react';
import type { KidGoal, KidWallet, MemberSummary, MoneyBucket } from '../../shared/types';

export const cx = (...parts: (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(' ');

/** The adult overview shape served by GET /api/kidmoney/summary (one row per child). */
export interface KidSummary {
  member: MemberSummary;
  wallet: KidWallet;
  goals: KidGoal[];
}

// ---------------------------------------------------------------- buckets

export interface BucketMeta {
  key: MoneyBucket;
  label: string;
  emoji: string;
  blurb: string;
  /** A CSS colour token. Used for bars and accents; always paired with the label. */
  accent: string;
  soft: string; // tailwind bg-*-soft class
  text: string; // tailwind text-* class
}

export const BUCKETS: BucketMeta[] = [
  {
    key: 'spend',
    label: 'Spend',
    emoji: '🛍️',
    blurb: 'Treats and things you want now',
    accent: 'var(--color-clay)',
    soft: 'bg-clay-soft',
    text: 'text-clay',
  },
  {
    key: 'save',
    label: 'Save',
    emoji: '🏦',
    blurb: 'Growing toward a goal',
    accent: 'var(--color-sage)',
    soft: 'bg-sage-soft',
    text: 'text-sage',
  },
  {
    key: 'give',
    label: 'Give',
    emoji: '💝',
    blurb: 'Sharing with others',
    accent: 'var(--color-dusk)',
    soft: 'bg-dusk-soft',
    text: 'text-dusk',
  },
];

export const bucketMeta = (key: MoneyBucket): BucketMeta =>
  BUCKETS.find((b) => b.key === key) ?? BUCKETS[0];

export const walletTotal = (w: KidWallet): number => w.spend + w.save + w.give;

/** The goal to feature on a card: the closest unfinished one, else a finished one. */
export function nearestGoal(goals: KidGoal[]): KidGoal | null {
  const open = goals.filter((g) => !g.achieved_at);
  if (open.length) return [...open].sort((a, b) => b.percent - a.percent)[0];
  return goals[0] ?? null;
}

// ---------------------------------------------------------------- buttons + fields

export const btnPrimary =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-sage px-4 text-sm font-medium text-white transition-colors hover:bg-sage/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:cursor-not-allowed disabled:opacity-50';

export const btnSecondary =
  'inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 text-sm font-medium text-ink transition-colors hover:bg-surface-sunk focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:cursor-not-allowed disabled:opacity-50';

export const fieldLabel = 'block text-sm font-medium text-ink';
export const fieldInput =
  'mt-1 block w-full min-h-[44px] rounded-xl border border-border bg-surface px-3 py-2 text-base text-ink placeholder:text-ink-faint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage';

// ---------------------------------------------------------------- members

export const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

export function Avatar({
  member,
  size = 'md',
}: {
  member: Pick<MemberSummary, 'name' | 'color'> | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dims =
    size === 'lg' ? 'h-14 w-14 text-xl' : size === 'sm' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm';
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-grid shrink-0 place-items-center rounded-full font-semibold text-white',
        dims
      )}
      style={{ backgroundColor: member?.color ?? 'var(--color-ink-faint)' }}
    >
      {member ? initials(member.name) : '?'}
    </span>
  );
}

// ---------------------------------------------------------------- shells

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={cx('rounded-card border border-border bg-surface p-5', className)}>
      {children}
    </section>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="font-display text-lg font-semibold text-ink">{children}</h2>
      {action}
    </div>
  );
}

export function LoadingState({ label = 'Counting your money…' }: { label?: string }) {
  return (
    <div role="status" className="space-y-3">
      <p className="text-sm text-ink-faint">{label}</p>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-24 animate-pulse rounded-card border border-border bg-surface-sunk"
        />
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card className="border-over/40 bg-clay-soft">
      <p className="font-medium text-ink">Something went wrong</p>
      <p className="mt-1 text-sm text-ink-soft">{message}</p>
      <button type="button" onClick={onRetry} className={cx(btnSecondary, 'mt-4')}>
        Try again
      </button>
    </Card>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <Card className="border-dashed text-center">
      <p className="font-display text-lg font-semibold text-ink">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-ink-soft">{body}</p>
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </Card>
  );
}

/** Inline feedback for something that just happened (or failed). */
export function Banner({ tone, message }: { tone: 'ok' | 'error'; message: string }) {
  return (
    <p
      role="status"
      className={cx(
        'rounded-xl border px-4 py-3 text-sm',
        tone === 'ok'
          ? 'border-sage/30 bg-sage-soft text-sage'
          : 'border-over/40 bg-clay-soft text-clay'
      )}
    >
      {tone === 'ok' ? '✓ ' : '! '}
      {message}
    </p>
  );
}

/** A centred dialog. Escape closes it; the backdrop click does too. */
export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const titleId = `dlg-${title.replace(/\W+/g, '-').toLowerCase()}`;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-card border border-border bg-surface p-5 shadow-xl sm:rounded-card"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={titleId} className="font-display text-lg font-semibold text-ink">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="-mt-1 -mr-1 grid h-11 w-11 place-items-center rounded-xl text-ink-soft hover:bg-surface-sunk hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            <span aria-hidden="true" className="text-lg leading-none">
              ✕
            </span>
            <span className="sr-only">Close</span>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- bars

/**
 * A bucket's share of the whole wallet, as a wide friendly bar. The percentage of
 * total is written out so the bar is never the only signal, and each bucket keeps
 * its own accent colour and emoji.
 */
export function BucketBar({ meta, amount, total }: { meta: BucketMeta; amount: number; total: number }) {
  const share = total > 0 ? Math.round((amount / total) * 100) : 0;
  return (
    <div
      className="h-2.5 w-full overflow-hidden rounded-full bg-surface-sunk"
      role="img"
      aria-label={`${meta.label}: ${share}% of the total`}
    >
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${Math.max(share, amount > 0 ? 6 : 0)}%`, backgroundColor: meta.accent }}
      />
    </div>
  );
}

/** A goal's fill bar toward its target, in the goal's bucket colour. */
export function GoalBar({ percent, accent }: { percent: number; accent: string }) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div
      className="h-3 w-full overflow-hidden rounded-full bg-surface-sunk"
      role="img"
      aria-label={`${clamped}% of the way there`}
    >
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${Math.max(clamped, percent > 0 ? 4 : 0)}%`, backgroundColor: accent }}
      />
    </div>
  );
}
